/**
 * The workspace's storage, reachable from every view (file areas §4.9:
 * nothing the user pays for is out of sight).
 *
 *   - `WorkspaceStorageStrip` — a compact usage bar for the toolbar; clicking
 *     it opens the panel.
 *   - `WorkspaceStoragePanel` — a side sheet with the full usage bar, what's
 *     using space (by area and arche, with history apart), the workspace's
 *     version-history setting, and Arche data (what arches keep besides
 *     files).
 *
 * These used to render only above Browse (and Arche data only on My Data),
 * so a Desktop or Search view would have hidden them.
 */
import { HardDrive } from 'lucide-react';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { VaultStorageUsage } from '@/components/files/VaultStorageUsage';
import { formatBytes } from '@/components/files/file-dialog/format';
import { useVaultDocumentsStorage } from '../hooks/useVaultDocuments';
import { StorageBreakdown } from './StorageBreakdown';
import { WorkspaceHistorySettings } from './WorkspaceHistorySettings';
import { ArcheDataSection } from './ArcheDataSection';

export function WorkspaceStorageStrip({
  holderId,
  onOpen,
  compact = false,
}: {
  holderId: string;
  onOpen: () => void;
  /** Just the icon (still opens the panel) — for a toolbar out of room. The
   *  figures move to the tooltip. */
  compact?: boolean;
}) {
  const query = useVaultDocumentsStorage(holderId);
  const principal = query.data?.principal;
  const pct =
    principal && principal.quotaBytes > 0
      ? Math.min(100, (principal.totalStorageBytes / principal.quotaBytes) * 100)
      : 0;
  const tone = pct >= 95 ? 'bg-destructive' : pct >= 80 ? 'bg-amber-500' : 'bg-primary';
  const over = !!principal && principal.totalStorageBytes > principal.quotaBytes;
  const figures = principal
    ? `${formatBytes(principal.totalStorageBytes)} of ${formatBytes(principal.quotaBytes)}`
    : null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex min-w-0 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted/40"
      title={
        compact && figures
          ? `Storage: ${figures} — version history and arche data`
          : 'Storage, version history and arche data'
      }
      aria-label={figures ? `Storage: ${figures}` : 'Storage'}
    >
      <HardDrive
        className={`h-4 w-4 shrink-0 ${compact && over ? 'text-amber-700 dark:text-amber-400' : ''}`}
        aria-hidden
      />
      {compact ? null : principal ? (
        <>
          <span className="hidden h-1.5 w-20 overflow-hidden rounded bg-muted sm:block">
            <span className={`block h-full ${tone}`} style={{ width: `${pct}%` }} />
          </span>
          <span className={`truncate ${over ? 'text-amber-700 dark:text-amber-400' : ''}`}>
            {figures}
          </span>
        </>
      ) : (
        <span>Storage</span>
      )}
    </button>
  );
}

export function WorkspaceStoragePanel({
  holderId,
  open,
  onOpenChange,
}: {
  holderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* The header (and its close button) stays put; only the body scrolls. */}
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Storage</SheetTitle>
          <SheetDescription>
            What this workspace’s files use, how version history works here, and what arches keep
            for you besides files.
          </SheetDescription>
        </SheetHeader>
        {open && (
          <SheetBody className="mt-4 flex flex-col gap-5">
            <VaultStorageUsage holderId={holderId} card={false} title="Storage used" />
            <StorageBreakdown holderId={holderId} />
            <WorkspaceHistorySettings holderId={holderId} />
            <ArcheDataSection holderId={holderId} />
          </SheetBody>
        )}
      </SheetContent>
    </Sheet>
  );
}
